import { axiosPost, axiosGet } from "./axiosFunctions";
import { getTokenOrLogout } from "./common";
import { getTempState } from "../../assets/scripts/login-util";

/**
 * Billing API (Phase 8).
 *
 * Exactly the shape of agentAPI.js: URL constants at the top, one
 * exported async function per endpoint, `getTokenOrLogout()` then
 * `axiosGet`/`axiosPost` with the bearer header.
 *
 * ==================== NOTHING HERE SENDS A PRICE ====================
 *
 * Every request carries plan CODES, seat COUNTS and promo CODES only.
 * The rupee amounts that come back are for DISPLAY; the server recomputes
 * them from the database at checkout and checks the captured amount
 * against its own figure. If this file ever starts posting an `amount`,
 * something has gone wrong upstream.
 */

const getPlansUrl           = '/api/billing/getPlans';
const getSubscriptionUrl    = '/api/billing/getSubscription';
const getEntitlementsUrl    = '/api/billing/getEntitlements';
const getUsageSummaryUrl    = '/api/billing/getUsageSummary';
const getLicensesUrl        = '/api/billing/getLicenses';
const validatePromoUrl      = '/api/billing/validatePromo';
const createCheckoutUrl     = '/api/billing/createCheckout';
const verifyPaymentUrl      = '/api/billing/verifyPayment';
const purchaseLicensesUrl   = '/api/billing/purchaseLicenses';
const removeLicensesUrl     = '/api/billing/removeLicenses';
const getInvoicesUrl        = '/api/billing/getInvoices';
const getOrderStatusUrl     = (orderKey)  => `/api/billing/getOrderStatus/${orderKey}`;
const getInvoiceUrl         = (invoiceId) => `/api/billing/getInvoice/${invoiceId}`;
const downloadInvoiceUrl    = (invoiceId) => `/api/billing/downloadInvoice/${invoiceId}`;
const getPaymentMethodUrl   = '/api/billing/getPaymentMethod';

/**
 * Prefers the full session token. During onboarding (components/setup/setup.js)
 * no full session exists yet - completeAgentSetup() is what creates one - so a
 * call made from the setup wizard falls back to the signup/setup token instead,
 * the same one agentAPI.js's createAgent/updateAgentSetup/completeAgentSetup
 * already use via getTempState('token'). Both are ordinary JWTs verified
 * identically by checkAuth; "temp" is a frontend cookie-naming convention, not
 * a different token type on the server. Existing callers with a full session
 * are unaffected - getTokenOrLogout() is tried first and wins whenever it has
 * a token.
 */
const auth = () => {
    const token = getTokenOrLogout() || getTempState('token');
    if (!token) setTimeout(() => { return }, 2000);
    return { headers: { auth_token: `bearer ${token}` } };
};

/**
 * A fresh idempotency key per checkout attempt.
 *
 * The server stores it against the order (uk_Idem_Key). Without one, a
 * double-clicked Pay button creates two payable orders at Razorpay and
 * the customer can be charged twice - so the key is generated ONCE per
 * attempt and reused on retries of that same attempt.
 *
 * crypto.randomUUID needs a secure context; the fallback keeps this
 * working on plain http during local development.
 */
export const newIdempotencyKey = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return `ib-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

/* ------------------------------------------------------------------ *
 * Reads
 * ------------------------------------------------------------------ */

export const getPlans = async () => {
    return await axiosGet(getPlansUrl, auth());
};

export const getSubscription = async () => {
    return await axiosGet(getSubscriptionUrl, auth());
};

export const getEntitlements = async () => {
    return await axiosGet(getEntitlementsUrl, auth());
};

/**
 * Task 9. Lightweight sibling of getEntitlements - just `{ quotas,
 * filesBySection }`, for the header Usage dropdown (usagePanel.js) so it
 * doesn't have to pull the full entitlement bundle just to show a
 * progress bar. Same allow-listing on the server as getEntitlements - an
 * over-quota tenant still needs to see why.
 */
export const getUsageSummary = async () => {
    return await axiosGet(getUsageSummaryUrl, auth());
};

/**
 * True if the resolved feature map grants `featureCode` right now.
 *
 * Task 6: the shared replacement for every place that used to read a
 * legacy TempSubscription flag (SubAgency/Team) via agentAPI.getAgentSubscription().
 * Sourced from the same entitlement bundle the server's own auth gate
 * uses, so it automatically reflects Free-plan restrictions once a
 * subscription is EXPIRED - see ibbackend/services/entitlementService.js.
 */
export const hasFeature = async (featureCode) => {
    const { features } = await getEntitlements();
    const feature = features.find(f => f.code === featureCode);
    return Boolean(feature && feature.granted);
};

export const getLicenses = async () => {
    return await axiosGet(getLicensesUrl, auth());
};

export const getInvoices = async (limit = 25, offset = 0) => {
    return await axiosGet(`${getInvoicesUrl}?limit=${limit}&offset=${offset}`, auth());
};

export const getInvoice = async (invoiceId) => {
    return await axiosGet(getInvoiceUrl(invoiceId), auth());
};

/** { url, expiresInSeconds } - a short-lived signed S3 link, minted fresh on every call. */
export const downloadInvoice = async (invoiceId) => {
    return await axiosGet(downloadInvoiceUrl(invoiceId), auth());
};

export const getOrderStatus = async (orderKey) => {
    return await axiosGet(getOrderStatusUrl(orderKey), auth());
};

/** { paymentMethod: null } for a tenant who has never paid - not an error, the normal FREE-plan state. */
export const getPaymentMethod = async () => {
    return await axiosGet(getPaymentMethodUrl, auth());
};

/* ------------------------------------------------------------------ *
 * Writes
 * ------------------------------------------------------------------ */

/** Advisory. Tells us what a code is worth; the charge is computed again at checkout. */
export const validatePromo = async ({ code, planCode, addOnLicenses = 0, intent = 'NEW' }) => {
    return await axiosPost(validatePromoUrl, { code, planCode, addOnLicenses, intent }, auth());
};

export const createCheckout = async ({ intent = 'NEW', planCode, addOnLicenses = 0, promoCode = null }, idempotencyKey) => {
    const config = auth();
    config.headers = { ...config.headers, 'Idempotency-Key': idempotencyKey };
    return await axiosPost(createCheckoutUrl, { intent, planCode, addOnLicenses, promoCode }, config);
};

/**
 * The fast path after Razorpay Checkout closes.
 *
 * A CONVENIENCE, not the source of truth. If this never arrives - the
 * browser was closed, the network dropped - the webhook completes the
 * activation server-side and paymentStatus polling picks it up.
 */
export const verifyPayment = async ({ orderKey, razorpay_order_id, razorpay_payment_id, razorpay_signature }) => {
    return await axiosPost(verifyPaymentUrl,
        { orderKey, razorpay_order_id, razorpay_payment_id, razorpay_signature }, auth());
};

export const purchaseLicenses = async ({ quantity, promoCode = null }, idempotencyKey) => {
    const config = auth();
    config.headers = { ...config.headers, 'Idempotency-Key': idempotencyKey };
    return await axiosPost(purchaseLicensesUrl, { quantity, promoCode }, config);
};

export const removeLicenses = async ({ quantity }) => {
    return await axiosPost(removeLicensesUrl, { quantity }, auth());
};

/* ------------------------------------------------------------------ *
 * Razorpay Checkout
 * ------------------------------------------------------------------ */

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * Loads Razorpay Checkout on demand.
 *
 * Deliberately NOT a <script> in public/index.html. Around 95% of
 * sessions never touch billing, and adding a third-party script to every
 * page load of the whole product to serve the other 5% is a cost paid by
 * everyone. Loaded once and cached by the browser thereafter.
 */
export const loadRazorpayCheckout = () => new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(window.Razorpay);

    const existing = document.querySelector(`script[src="${RAZORPAY_SRC}"]`);
    if (existing) {
        existing.addEventListener('load', () => resolve(window.Razorpay));
        existing.addEventListener('error', () => reject(new Error('Could not load the payment window. Check your connection and try again.')));
        return;
    }

    const script = document.createElement('script');
    script.src   = RAZORPAY_SRC;
    script.async = true;
    script.onload  = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Could not load the payment window. Check your connection and try again.'));
    document.body.appendChild(script);
});

/** Paise -> '₹11,999.00'. Mirrors services/money.js formatINR on the server. */
export const formatINR = (paise) => {
    const n = Number(paise || 0) / 100;
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
